import { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import type { Cart } from '../../../types';

interface SuspendedCartsPanelProps {
  open: boolean;
  onClose: () => void;
  suspendedCarts: Cart[];
  isLoading: boolean;
  onResume: (cartId: string) => Promise<void>;
  onDelete: (cartId: string) => Promise<void>;
  formatPrice: (price: number) => string;
  maxCarts?: number;
}

export const SuspendedCartsPanel = ({
  open,
  onClose,
  suspendedCarts,
  isLoading,
  onResume,
  onDelete,
  formatPrice,
  maxCarts = 20,
}: SuspendedCartsPanelProps) => {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleResume = async (cartId: string) => {
    setProcessingId(cartId);
    setError(null);
    try {
      await onResume(cartId);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la reprise du panier');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (cartId: string) => {
    if (!window.confirm('Supprimer ce panier en attente ?')) return;
    setProcessingId(cartId);
    setError(null);
    try {
      await onDelete(cartId);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    } finally {
      setProcessingId(null);
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'maintenant';
    if (diffMins < 60) return `il y a ${diffMins}min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `il y a ${diffDays}j`;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 }, maxWidth: '100%' }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PauseCircleOutlineIcon color="warning" />
          <Typography variant="h6">
            Paniers en attente
          </Typography>
          <Chip
            size="small"
            label={`${suspendedCarts.length}/${maxCarts}`}
            color={suspendedCarts.length >= maxCarts ? 'error' : 'default'}
          />
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : suspendedCarts.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <PauseCircleOutlineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            Aucun panier en attente
          </Typography>
        </Box>
      ) : (
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {suspendedCarts.map((cart) => (
            <ListItem
              key={cart.id}
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                opacity: processingId === cart.id ? 0.5 : 1,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="subtitle2">
                    {cart.customerName || 'Client anonyme'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">
                    {cart.suspendedAt && formatTimeAgo(cart.suspendedAt)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {cart.totalItems} article{cart.totalItems > 1 ? 's' : ''}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formatPrice(cart.totalAmount)}
                </Typography>
              </Box>

              {cart.suspensionNotes && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    bgcolor: 'action.hover',
                    p: 1,
                    borderRadius: 1,
                    mb: 1,
                    fontStyle: 'italic',
                  }}
                >
                  {cart.suspensionNotes}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={processingId === cart.id ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                  onClick={() => handleResume(cart.id)}
                  disabled={processingId !== null}
                  sx={{ flex: 1 }}
                >
                  Reprendre
                </Button>
                <Tooltip title="Supprimer">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(cart.id)}
                    disabled={processingId !== null}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItem>
          ))}
        </List>
      )}
    </Drawer>
  );
};
